/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1686268800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Jun 09 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "acoustic",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Acoustic",
          "depth": 0,
          "id": "connect-to-acoustic"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing relational tables or databases",
          "depth": 1,
          "id": "syncing-relational-tables-or-databases"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      p: "p",
      strong: "strong",
      ul: "ul",
      li: "li",
      code: "code",
      img: "img",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {PremiumDestination, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!PremiumDestination) _missingMdxReference("PremiumDestination", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(PremiumDestination, {
        destinationName: "Acoustic"
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Any data set"
            }), _jsx(_components.td, {
              children: "Sync data from any source to an Acoustic relational table or database"
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "https://help.goacoustic.com/hc/en-us/articles/360043347953-Relational-tables",
                children: "Relational table docs"
              }), ", ", _jsx(_components.a, {
                href: "https://help.goacoustic.com/hc/en-us/articles/360042858454-Import-a-database",
                children: "Database docs"
              })]
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-acoustic",
        children: "Connect to Acoustic"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Acoustic"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Acoustic"
        }), " by entering the following required fields into Hightouch:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Pod"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Client Token"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Client Secret"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Refresh Token"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can find your Pod, for example, ", _jsx(_components.code, {
          children: "6"
        }), ", in your ", _jsx(_components.a, {
          href: "https://cloud.goacoustic.com/settings",
          children: "Acoustic Account Settings"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-acoustic-pod.png",
          alt: "Acoustic pod"
        })
      }), "\n", _jsx(_components.p, {
        children: "If you know the region but are unsure of your pod, you can use the table below."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Campaign URL"
            }), _jsx(_components.th, {
              children: "Pod"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "api-campaign-us-1.goacoustic.com"
            }), _jsx(_components.td, {
              children: "1"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "api-campaign-us-2.goacoustic.com"
            }), _jsx(_components.td, {
              children: "2"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "api-campaign-us-3.goacoustic.com"
            }), _jsx(_components.td, {
              children: "3"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "api-campaign-us-4.goacoustic.com"
            }), _jsx(_components.td, {
              children: "4"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "api-campaign-us-5.goacoustic.com"
            }), _jsx(_components.td, {
              children: "5"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "api-campaign-us-6.goacoustic.com"
            }), _jsx(_components.td, {
              children: "6"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "api-campaign-eu-1.goacoustic.com"
            }), _jsx(_components.td, {
              children: "7"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "api-campaign-ap-2.goacoustic.com"
            }), _jsx(_components.td, {
              children: "8"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "api-campaign-us-6.goacoustic.com"
            }), _jsx(_components.td, {
              children: "9"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "api-campaign-ap-1.goacoustic.com"
            }), _jsx(_components.td, {
              children: "A"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "api-campaign-ap-3.goacoustic.com"
            }), _jsx(_components.td, {
              children: "B"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "api-campaign-pilot.goacoustic.com"
            }), _jsx(_components.td, {
              children: "PILOT"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["You can generate the other credentials in the ", _jsx(_components.strong, {
          children: "Application Account Access"
        }), " section of your ", _jsx(_components.a, {
          href: "https://cloud.goacoustic.com/campaign-automation/Settings/Administration/Organization_settings",
          children: "Acoustic Organization Settings"
        }), ".\nSee ", _jsx(_components.a, {
          href: "https://developer.goacoustic.com/acoustic-campaign/reference/getting-started-with-oauth#step-one-defining-your-api-application",
          children: "these instructions"
        }), " for more details."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Acoustic destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Acoustic destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-relational-tables-or-databases",
        children: "Syncing relational tables or databases"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch requires an existing ", _jsx(_components.a, {
          href: "https://help.goacoustic.com/hc/en-us/articles/360042858454-Import-a-database",
          children: "database"
        }), " or ", _jsx(_components.a, {
          href: "https://help.goacoustic.com/hc/en-us/articles/360043347953-Relational-tables",
          children: "relational table"
        }), " in Acoustic to sync to.\nThe only supported ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync mode"
        }), " is ", _jsx(_components.strong, {
          children: "Upsert"
        }), ".\nIn Upsert mode, Hightouch inserts new records and keeps ", _jsx(_components.a, {
          href: "#field-mapping",
          children: "mapped attributes"
        }), " up-to-date on existing records in Acoustic."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows from your model to rows in Acoustic on any column in your model and any ", _jsx(_components.a, {
          href: "https://help.goacoustic.com/hc/en-us/articles/360042859634-Unique-identifiers",
          children: "key"
        }), " fields in Acoustic.\nRefer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync model columns via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to any existing columns in your Acoustic database or relational table.\nEnsure your model columns data types match the data types of the fields you want to sync to."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the record in Acoustic with all its synced fields"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Delete the synced records from Acoustic"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "If you enable deletion, Hightouch provides all columns to Acoustic in the deletion API request.\nThis enables Hightouch to uniquely identify a single record, rather than a single key column, which could match multiple records."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
